import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import {
  FloorplanFreeTextListType,
  FREE_TEXT_DEFAULT_FONT_SIZE,
  FREE_TEXT_MAX_FONT_SIZE,
  FREE_TEXT_MAX_TEXT_BOX_HEIGHT,
  FREE_TEXT_MAX_TEXT_BOX_WIDTH,
  FREE_TEXT_MIN_FONT_SIZE,
  FREE_TEXT_MIN_TEXT_BOX_HEIGHT,
  FREE_TEXT_MIN_TEXT_BOX_WIDTH,
} from "@lib/constants/floorplan-free-text";
import { Exclude, Expose } from "class-transformer";
import { IsBoolean, IsEnum, IsInt, IsString, Matches, Max, MaxLength, Min } from "class-validator";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export class FloorplanFreeText extends FloorplanItem {
  @Exclude()
  readonly fontSizeChanged$ = new Subject<number>();

  @IsString()
  @MaxLength(2000, { message: "Free text must not exceed 2000 characters in length" })
  @Expose({ name: "content" })
  private _content: string;

  @IsString()
  @Matches(/^#[0-9A-Fa-f]{6}$/, {
    message: "Text color must be a six-digit hexadecimal color",
  })
  @Expose({ name: "textColor" })
  private _textColor: string = "#002766";

  @IsBoolean()
  @Expose({ name: "bold" })
  private _bold = false;

  @IsBoolean()
  @Expose({ name: "italic" })
  private _italic = false;

  @IsInt()
  @Min(FREE_TEXT_MIN_FONT_SIZE)
  @Max(FREE_TEXT_MAX_FONT_SIZE)
  @Expose({ name: "fontSize" })
  private _fontSize = FREE_TEXT_DEFAULT_FONT_SIZE;

  @IsEnum(FloorplanFreeTextListType)
  @Expose({ name: "listType" })
  private _listType = FloorplanFreeTextListType.NONE;

  @IsInt()
  @Min(FREE_TEXT_MIN_TEXT_BOX_WIDTH)
  @Max(FREE_TEXT_MAX_TEXT_BOX_WIDTH)
  @Expose({ name: "textBoxWidth" })
  private _textBoxWidth?: number;

  @IsInt()
  @Min(FREE_TEXT_MIN_TEXT_BOX_HEIGHT)
  @Max(FREE_TEXT_MAX_TEXT_BOX_HEIGHT)
  @Expose({ name: "textBoxHeight" })
  private _textBoxHeight?: number;

  constructor(floorplan: Floorplan, id: string, x: number, y: number, content: string = "") {
    super(floorplan, id, x, y);
    this._content = content;
  }

  get content(): string {
    return this._content;
  }

  set content(value: string) {
    if (this._content === value) return;
    this._content = value;
    this._floorplan.publishUpdate(FloorplanEventType.FREE_TEXT_UPDATED, this);
  }

  get textColor(): string {
    return this._textColor;
  }

  set textColor(value: string) {
    if (this._textColor === value) return;
    this._textColor = value;
    this._floorplan.publishUpdate(FloorplanEventType.FREE_TEXT_UPDATED, this);
  }

  get bold(): boolean {
    return this._bold;
  }

  set bold(value: boolean) {
    if (this._bold === value) return;
    this._bold = value;
    this._floorplan.publishUpdate(FloorplanEventType.FREE_TEXT_UPDATED, this);
  }

  get italic(): boolean {
    return this._italic;
  }

  set italic(value: boolean) {
    if (this._italic === value) return;
    this._italic = value;
    this._floorplan.publishUpdate(FloorplanEventType.FREE_TEXT_UPDATED, this);
  }

  get fontSize(): number {
    return this._fontSize;
  }

  set fontSize(value: number) {
    if (!Number.isInteger(value) || value < FREE_TEXT_MIN_FONT_SIZE || value > FREE_TEXT_MAX_FONT_SIZE) {
      return;
    }
    if (this._fontSize === value) return;

    this._fontSize = value;
    this.fontSizeChanged$.next(value);
    this._floorplan.publishUpdate(FloorplanEventType.FREE_TEXT_UPDATED, this);
  }

  get listType(): FloorplanFreeTextListType {
    return this._listType;
  }

  set listType(value: FloorplanFreeTextListType) {
    if (!Object.values(FloorplanFreeTextListType).includes(value)) {
      return;
    }
    if (this._listType === value) return;

    this._listType = value;
    this._floorplan.publishUpdate(FloorplanEventType.FREE_TEXT_UPDATED, this);
  }

  get textBoxWidth(): number | undefined {
    return this._textBoxWidth;
  }

  set textBoxWidth(value: number | undefined) {
    if (
      value !== undefined &&
      (!Number.isInteger(value) || value < FREE_TEXT_MIN_TEXT_BOX_WIDTH || value > FREE_TEXT_MAX_TEXT_BOX_WIDTH)
    ) {
      return;
    }
    if (this._textBoxWidth === value) return;

    this._textBoxWidth = value;
    this._floorplan.publishUpdate(FloorplanEventType.FREE_TEXT_UPDATED, this);
  }

  get textBoxHeight(): number | undefined {
    return this._textBoxHeight;
  }

  set textBoxHeight(value: number | undefined) {
    if (
      value !== undefined &&
      (!Number.isInteger(value) || value < FREE_TEXT_MIN_TEXT_BOX_HEIGHT || value > FREE_TEXT_MAX_TEXT_BOX_HEIGHT)
    ) {
      return;
    }
    if (this._textBoxHeight === value) return;

    this._textBoxHeight = value;
    this._floorplan.publishUpdate(FloorplanEventType.FREE_TEXT_UPDATED, this);
  }

  hasContent(): boolean {
    return this._content.trim().length > 0;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.floorplanFreeTextsItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const content = this.content;
    const textColor = this.textColor;
    const bold = this.bold;
    const italic = this.italic;
    const fontSize = this.fontSize;
    const listType = this.listType;
    const textBoxWidth = this.textBoxWidth;
    const textBoxHeight = this.textBoxHeight;

    return (x: number, y: number) => {
      const duplicate = FloorplanFreeText.create(this._floorplan, x, y, content);
      duplicate.textColor = textColor;
      duplicate.bold = bold;
      duplicate.italic = italic;
      duplicate.fontSize = fontSize;
      duplicate.listType = listType;
      duplicate.textBoxWidth = textBoxWidth;
      duplicate.textBoxHeight = textBoxHeight;
      return duplicate;
    };
  }

  override delete() {
    this._floorplan.deleteFloorplanFreeText(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.floorplanFreeTexts;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanFreeText,
        this._floorplan.floorplanFreeTexts,
        FloorplanEventType.FREE_TEXT_ADDED,
        () => this._floorplan.project.setFloorplanFreeTextPositionIds(),
        afterDuplicateAdded as ((duplicate: FloorplanFreeText) => void) | undefined,
      );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshFloorplanFreeTextsLock();
  }

  static create(floorplan: Floorplan, x: number, y: number, content: string = "") {
    return new FloorplanFreeText(floorplan, uuidv4(), x, y, content);
  }
}
